import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  rating: number;
  review: string;
  image: string;
  alt: string;
  date: string;
}

interface SocialProofProps {
  testimonials: Testimonial[];
}

export default function SocialProof({ testimonials }: SocialProofProps) {
  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-success/10 px-4 py-2 rounded-full mb-4">
            <Icon name="HeartIcon" size={20} variant="solid" className="text-success" />
            <span className="text-sm font-semibold text-success">Loved by Millions</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-4">
            What Our Listeners Say
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Join 10 lakh+ happy listeners who've made StoryNest-FM their daily audio companion
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-card rounded-2xl p-6 shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-2"
            >
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon
                    key={i}
                    name="StarIcon"
                    size={18}
                    variant="solid"
                    className={i < testimonial.rating ? 'text-accent' : 'text-muted'}
                  />
                ))}
              </div>

              <p className="text-text-primary mb-6 line-clamp-4">"{testimonial.review}"</p>

              <div className="flex items-center space-x-3 pt-4 border-t border-border">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                    src={testimonial.image}
                    alt={testimonial.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-text-primary truncate">{testimonial.name}</p>
                  <p className="text-sm text-text-secondary truncate">{testimonial.location}</p>
                </div>
                <p className="text-xs text-text-secondary flex-shrink-0">{testimonial.date}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-primary to-primary-dark rounded-3xl p-8 md:p-12 text-center shadow-warm-lg">
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center justify-center space-x-8 mb-6">
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">4.8</p>
                <div className="flex items-center justify-center space-x-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Icon
                      key={i}
                      name="StarIcon"
                      size={20}
                      variant="solid"
                      className="text-accent"
                    />
                  ))}
                </div>
                <p className="text-sm text-white/80">App Store Rating</p>
              </div>

              <div className="h-16 w-px bg-white/20" />

              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">10L+</p>
                <p className="text-sm text-white/80">Downloads</p>
              </div>

              <div className="h-16 w-px bg-white/20" />

              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white mb-2">50K+</p>
                <p className="text-sm text-white/80">5-Star Reviews</p>
              </div>
            </div>

            <h3 className="text-2xl md:text-3xl font-heading font-bold text-white">
              Join India's Fastest Growing Audio Community
            </h3>
            <p className="text-lg text-white/90">
              Download the app today and get 7 days of premium access absolutely free!
            </p>

            <a
              href="/app-download"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-primary rounded-xl font-cta font-bold text-lg transition-all duration-300 hover:bg-white/90 hover:shadow-warm-lg hover:scale-105"
            >
              <Icon name="ArrowDownTrayIcon" size={24} />
              <span>Download Now - It's Free!</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}